"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { Download, FileText, Image as ImageIcon, Search } from "lucide-react";
import { FilterBar } from "@/components/ui/filter-bar";
import { resolvePublicAssetUrl, type PublicResourceItem } from "@/lib/api";

interface FreeResourcesListClientProps {
  resources: PublicResourceItem[];
  categories: string[];
}

function formatFileSize(fileSize?: number | null) {
  if (!fileSize) return null;
  if (fileSize < 1024) return `${fileSize} KB`;
  return `${(fileSize / 1024).toFixed(1)} MB`;
}

export function FreeResourcesListClient({ resources, categories }: FreeResourcesListClientProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const formats = useMemo(() => {
    return Array.from(new Set(resources.map((item) => item.fileTypeLabel).filter(Boolean) as string[])).sort();
  }, [resources]);

  const filtered = useMemo(() => {
    let result = resources;

    if (selectedCategory) {
      result = result.filter((item) => item.category === selectedCategory);
    }

    if (selectedFormat) {
      result = result.filter((item) => item.fileTypeLabel === selectedFormat);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) =>
        item.title.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query) ||
        item.fileTypeLabel?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [resources, searchQuery, selectedCategory, selectedFormat]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex flex-col gap-4 mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search free resources..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <FilterBar tags={categories} selectedTag={selectedCategory} onTagChange={setSelectedCategory} label="Category" />
          <FilterBar tags={formats} selectedTag={selectedFormat} onTagChange={setSelectedFormat} label="Format" />
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-6">
        {filtered.length} {filtered.length === 1 ? "resource" : "resources"} found
      </p>

      {filtered.length === 0 ? (
        <div className="text-center py-14">
          <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No resources found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((resource) => (
            <article id={resource.slug} key={resource.id} className="flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card hover:border-primary/25 hover:shadow-lg transition-all duration-300">
              {resource.thumbnailPath ? (
                <div className="aspect-[16/9] overflow-hidden bg-muted">
                  <img src={resolvePublicAssetUrl(resource.thumbnailPath)} alt={resource.title} className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="aspect-[16/9] flex items-center justify-center bg-gradient-to-br from-sky-500/10 to-cyan-500/10 text-primary/40">
                  <ImageIcon className="h-12 w-12" />
                </div>
              )}
              <div className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {resource.category ? <span className="rounded-full bg-muted px-2.5 py-1">{resource.category}</span> : null}
                  {resource.fileTypeLabel ? <span className="rounded-full bg-muted px-2.5 py-1">{resource.fileTypeLabel}</span> : null}
                  {formatFileSize(resource.fileSize) ? <span className="rounded-full bg-muted px-2.5 py-1">{formatFileSize(resource.fileSize)}</span> : null}
                </div>
                <h2 className="text-xl font-semibold text-foreground">{resource.title}</h2>
                {resource.description ? <p className="mt-3 flex-1 text-sm leading-7 text-muted-foreground">{resource.description}</p> : <div className="flex-1" />}
                <Link
                  href={resolvePublicAssetUrl(resource.filePath)}
                  target="_blank"
                  rel="noreferrer"
                  download
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary"
                >
                  <Download className="h-4 w-4" />
                  Download Resource
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}